/**
 * A module loaded on the client only, exposed as reactive state. `ready` narrows `imports`:
 *
 *   const lazyMarkdown = lazy(import.meta.env.SSR ? undefined : () => import('…/Markdown.svelte'))
 *   {#if lazyMarkdown.ready}
 *     {@const { default: Markdown } = lazyMarkdown.imports}
 *
 * The SSR ternary has to sit at the call site: Vite folds it away before Rollup ever sees the `import()`,
 * which is what keeps the module out of the SSR bundle. Wrapping the `import()` in a function leaves it in.
 */
export type Lazy<T> =
  | { readonly ready: false; readonly imports: undefined }
  | { readonly ready: true; readonly imports: T }

export function lazy<T>(load: (() => Promise<T>) | undefined): Lazy<T> {
  let imports = $state<T>()

  $effect(() => {
    let cancelled = false
    // Only undefined on the server, where effects never run.
    void load!().then((loaded) => {
      if (!cancelled) {
        imports = loaded
      }
    })
    return () => {
      cancelled = true
    }
  })

  return {
    get ready() {
      return imports !== undefined
    },
    get imports() {
      return imports
    },
  } as Lazy<T>
}
